import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { type WorkflowDetail, answerWorkflow, fetchWorkflow } from '@/lib/workflows'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'

const OTHER = '__other__'

/** Chief of Staff's multiple-choice questions before building: pick, or write your own, then lock it in. */
export function BuildQuestions({ wf }: { wf: WorkflowDetail }) {
  const [picks, setPicks] = useState<Record<string, string[]>>({})
  const [other, setOther] = useState<Record<string, string>>({})
  const [sending, setSending] = useState(false)

  if (wf.build_status !== 'asking' || wf.questions.length === 0)
    return (
      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {wf.build_status === 'building'
          ? 'Answered. The team is building it now.'
          : 'These questions were answered.'}
      </p>
    )

  const answersFor = (id: string) => {
    const chosen = (picks[id] ?? []).filter((p) => p !== OTHER)
    const typed = (other[id] ?? '').trim()
    return (picks[id] ?? []).includes(OTHER) && typed ? [...chosen, typed] : chosen
  }
  const ready = wf.questions.every((q) => answersFor(q.id).length > 0)

  function toggle(id: string, label: string, multi: boolean) {
    setPicks((p) => {
      const cur = p[id] ?? []
      if (!multi) return { ...p, [id]: cur.includes(label) ? [] : [label] }
      return { ...p, [id]: cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label] }
    })
  }

  async function submit() {
    if (!ready || sending) return
    setSending(true)
    try {
      const answers = Object.fromEntries(wf.questions.map((q) => [q.id, answersFor(q.id)]))
      queryClient.setQueryData(['workflow', wf.id], await answerWorkflow(wf.id, answers))
      await queryClient.invalidateQueries({ queryKey: ['workflows'] })
    } catch (err) {
      notifyError('Could not send your answers', err)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      {wf.questions.map((q, n) => {
        const chosen = picks[q.id] ?? []
        return (
          <fieldset key={q.id}>
            <legend className="text-sm font-bold text-neutral-900 dark:text-white">
              {n + 1}. {q.question}
              {q.multi && (
                <span className="ml-1.5 text-xs font-normal text-neutral-500 dark:text-neutral-400">
                  Pick any
                </span>
              )}
            </legend>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[...q.options, { label: OTHER, hint: '' }].map((o) => {
                const on = chosen.includes(o.label)
                return (
                  <button
                    key={o.label}
                    type="button"
                    aria-pressed={on}
                    title={o.hint || undefined}
                    onClick={() => toggle(q.id, o.label, q.multi)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-left text-sm font-semibold transition-all active:scale-95',
                      on
                        ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                        : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-500'
                    )}
                  >
                    {on && <Check className="size-3.5" />}
                    {o.label === OTHER ? 'Something else…' : o.label}
                  </button>
                )
              })}
            </div>
            {q.options.some((o) => chosen.includes(o.label) && o.hint) && (
              <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                {q.options
                  .filter((o) => chosen.includes(o.label) && o.hint)
                  .map((o) => o.hint)
                  .join(' · ')}
              </p>
            )}
            {chosen.includes(OTHER) && (
              <input
                value={other[q.id] ?? ''}
                maxLength={300}
                placeholder="Say it in your words"
                onChange={(e) => setOther((o) => ({ ...o, [q.id]: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                className="mt-2 w-full rounded-[24px] border border-neutral-200 bg-white px-3.5 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:focus:border-neutral-600"
              />
            )}
          </fieldset>
        )
      })}
      <button
        type="button"
        disabled={!ready || sending}
        onClick={submit}
        className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-40 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
      >
        {sending ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
        Lock it in and build
      </button>
    </div>
  )
}

/** The questions as they appear in the chat, under Chief of Staff's message. */
export function ChatBuildQuestions({ id }: { id: number }) {
  const wf = useQuery({ queryKey: ['workflow', id], queryFn: () => fetchWorkflow(id), retry: false })
  if (wf.error)
    return <p className="text-xs text-neutral-500 dark:text-neutral-400">This workflow was deleted.</p>
  if (!wf.data)
    return <div className="h-24 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
  return (
    <div className="rounded-[24px] border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
      <BuildQuestions wf={wf.data} />
    </div>
  )
}
